import { useColumbiaErrors } from '@ben/columbia/contexts';
import { OutputValueType } from '@ben/orion/utils/calculated-value';
import Select from '@ben/orion/deprecated-select';
import Input, { InputVariant } from '@ben/orion/input';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../../shared/constants';

interface IModularCalculatedValueInputProps {
  componentControllerName: string;
}

const ModularCalculatedValueInput = ({
  componentControllerName,
}: IModularCalculatedValueInputProps) => {
  const { control, setValue } = useFormContext();
  const { getFirstErrorMessage } = useColumbiaErrors();

  const formulaServerError = getFirstErrorMessage(
    `${componentControllerName}.formula`
  );

  const outputValueType = useWatch({
    control,
    name: `${componentControllerName}.outputValueType`,
    exact: true,
  });

  return (
    <>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.outputValueType`}
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              name="Output value type"
              options={[
                { key: OutputValueType.PERCENTAGE, label: 'Percentage' },
                { key: OutputValueType.MONEY, label: 'Monetary value' },
                { key: OutputValueType.NUMBER, label: 'Number' },
                { key: OutputValueType.TEXT, label: 'Text' },
              ]}
              defaultValue={field.value}
              width="100%"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
        <Controller
          name={`${componentControllerName}.label`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Value label"
              hintText="Will be shown on top of the value, and read to screen-reader user before reading it."
              maxLength={30}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              width="100%"
              disabled={!outputValueType}
              onChange={(event) => {
                field.onChange(event);
                setValue(
                  `${componentControllerName}.labelEn`,
                  event.target.value
                );
              }}
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
      </ComponentsWrapper>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.valueDescription`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Description (optional)"
              variant={InputVariant.TextArea}
              maxLength={100}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              height="94px"
              width="100%"
              onChange={(event) => {
                field.onChange(event);
                setValue(
                  `${componentControllerName}.valueDescriptionEn`,
                  event.target.value
                );
              }}
            />
          )}
        />
      </ComponentsWrapper>
      <ComponentsWrapper>
        <Controller
          name={`${componentControllerName}.formula`}
          control={control}
          render={({ field, fieldState }) => {
            const hasError = !!fieldState.error || !!formulaServerError;
            const errorMessage =
              fieldState.error?.message ?? formulaServerError;

            return (
              <Input
                {...field}
                name="Formula"
                variant={InputVariant.TextArea}
                hintText="The formula will determine what to dynamically show to the user."
                hasError={hasError}
                errorMessage={errorMessage}
                width="100%"
              />
            );
          }}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
        />
      </ComponentsWrapper>
    </>
  );
};

export default ModularCalculatedValueInput;

const ComponentsWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;
